import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Award, BadgePercent, BarChart3, ShieldCheck, Wallet, Globe2, FileText, ArrowRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PROGRAMMES, INCLUDES, LEVELS, LEGAL_DOCS, eur } from "@/data/ambassadorProgramme";

const BENEFITS = [
  { icon: Wallet, title: "Commission on every paid enrolment", text: "€10 per CSCE and €25 per CSP enrolment, rising to €15 and €37.50 at the Enhanced rate after 25 paid enrolments in 12 months." },
  { icon: BadgePercent, title: "5% off for your network", text: "Your personal code gives learners 5% off. The saving is shown automatically at checkout." },
  { icon: BarChart3, title: "Your own live dashboard", text: "Track visits, registrations, enrolments, revenue and commissions in real time, plus a ready-made marketing kit with posts and images." },
  { icon: Award, title: "Certificate and digital badge", text: "A Digital Ambassador Certificate and badge for LinkedIn, plus an optional listing in the CSI Ambassador Directory." },
  { icon: Globe2, title: "International and free to join", text: "Open to professionals worldwide. No fees, targets or upfront costs. Weekly payouts by Wise Business in your currency." },
  { icon: ShieldCheck, title: "Fair and transparent", text: "Clear rules, automated tracking and fraud protection, so every genuine referral is credited to the right ambassador." },
];
const STEPS = ["Apply online", "CSI reviews and approves", "Share your link and code", "Learners enrol and pay", "Refund period ends (14 days)", "Paid every Friday via Wise"];

export const LegalLinks = () => (
  <div className="flex flex-wrap gap-2" data-testid="ambassador-legal-links">
    {LEGAL_DOCS.map((d) => (
      <Link key={d.slug} to={`/ambassadors/legal/${d.slug}`} className="chip text-xs inline-flex items-center gap-1" data-testid={`legal-link-${d.slug}`}><FileText size={12}/> {d.title}</Link>
    ))}
  </div>
);

const useExamCourseIds = () => {
  const [ids, setIds] = useState({});
  useEffect(() => {
    api.get("/courses").then((r) => setIds(Object.fromEntries(r.data.filter((c) => c.code?.endsWith("-EXAM")).map((c) => [c.code, c.slug || c.id])))).catch(() => {});
  }, []);
  return ids;
};

const CommissionTable = () => {
  const ids = useExamCourseIds();
  return (
  <div className="overflow-x-auto card-warm" data-testid="ambassador-commission-table">
    <table className="w-full text-sm min-w-[560px]">
      <thead className="bg-[var(--muted-surface)] text-left">
        <tr><th className="p-3">Programme</th><th className="p-3">Standard price</th><th className="p-3">Learner price</th><th className="p-3">Standard commission</th><th className="p-3">Enhanced (25+)</th></tr>
      </thead>
      <tbody>
        {PROGRAMMES.map((p) => (
          <tr key={p.code} className="border-t border-[var(--border-warm)]">
            <td className="p-3">
              {ids[`${p.code}-EXAM`] ? (
                <Link to={`/courses/${ids[`${p.code}-EXAM`]}`} className="group" data-testid={`commission-table-link-${p.code.toLowerCase()}`}>
                  <b className="group-hover:underline">{p.code} Exam Only</b> <ArrowRight size={12} className="inline text-[var(--gold-deep)]"/>
                  <div className="text-xs text-zinc-500">{p.name}</div>
                </Link>
              ) : <><b>{p.code}</b><div className="text-xs text-zinc-500">{p.name}</div></>}
            </td>
            <td className="p-3">{eur(p.price)}</td>
            <td className="p-3 text-emerald-700 font-semibold">{eur(p.learner)}</td>
            <td className="p-3 font-display text-lg text-[var(--gold-deep)]">{eur(p.standard)}</td>
            <td className="p-3 font-display text-lg text-[var(--gold-deep)]">{eur(p.enhanced)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="p-3 text-xs text-zinc-500 border-t border-[var(--border-warm)]">Both programmes include: {INCLUDES.join(" · ")}.</div>
  </div>
  );
};

export default function AmbassadorsPage() {
  useSeo({ title: "CSI Ambassador Programme - Refer and Earn", description: "Become a CSI Ambassador: give your network 5% off CSCE and CSP certifications and earn commission on every paid enrolment. Free to join, paid weekly via Wise.", path: "/ambassadors" });
  return (
    <div data-testid="ambassadors-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10">
        <div className="eyebrow mb-2">CSI Ambassador Programme</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl">Grow converged security worldwide, and earn as you do it.</h1>
        <p className="text-base text-zinc-700 mt-4 max-w-3xl">Recommend the CSCE and CSP certifications to your network. They save 5%, and you earn commission on every paid enrolment. No fees, no targets, no risk.</p>
        <div className="flex flex-wrap gap-3 mt-6">
          <Link to="/ambassadors/apply" className="btn-terracotta px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="ambassadors-apply-cta">Apply to become an Ambassador <ArrowRight size={14}/></Link>
          <Link to="/ambassadors/directory" className="chip px-5 py-3 text-sm" data-testid="ambassadors-directory-cta">Ambassador Directory</Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {BENEFITS.map((b, i) => (
          <div key={b.title} className="card-warm p-5 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
            <b.icon size={20} className="text-[var(--gold-deep)]"/>
            <h3 className="font-display text-xl mt-3">{b.title}</h3>
            <p className="text-sm text-zinc-700 mt-1">{b.text}</p>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <h2 className="font-display text-3xl mb-4">Commission and learner pricing</h2>
        <CommissionTable/>
        <p className="text-xs text-zinc-500 mt-2">The Enhanced rate applies automatically while you have 25 or more paid enrolments in the preceding 12 months.</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 grid lg:grid-cols-2 gap-6">
        <div>
          <h2 className="font-display text-3xl mb-4">How it works</h2>
          <ol className="space-y-2">
            {STEPS.map((s, i) => (
              <li key={s} className="flex items-center gap-3 text-sm"><span className="w-7 h-7 shrink-0 rounded-full bg-[var(--navy)] text-white grid place-items-center text-xs font-bold">{i + 1}</span>{s}</li>
            ))}
          </ol>
          <h3 className="font-display text-xl mt-6 mb-2">Weekly payouts</h3>
          <div className="card-warm px-4 py-3 text-sm" data-testid="ambassador-payout-schedule">
            <b>Every Friday at 18:00 Spain time</b>, via Wise Business, for all commissions approved by then. Minimum payout €50; smaller balances roll forward to the next Friday.
          </div>
        </div>
        <div>
          <h2 className="font-display text-3xl mb-4">Recognition levels</h2>
          <div className="grid grid-cols-2 gap-3">
            {LEVELS.map((l) => (
              <div key={l.name} className="card-warm p-4" data-testid={`level-${l.name.toLowerCase()}`}>
                <div className="w-3 h-3 rounded-full" style={{ background: l.color }}/>
                <div className="font-display text-xl mt-2">{l.name}</div>
                <div className="text-xs text-zinc-500">{l.range} paid enrolments / year</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-2">Levels are for recognition and are shown on your dashboard and in the directory.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="card-warm p-6">
          <h2 className="font-display text-2xl">Programme documents</h2>
          <p className="text-sm text-zinc-700 mt-1 mb-4">Ambassadors are independent referral partners: there is no employment relationship and no guaranteed earnings. CSI may change or end the programme at any time and keeps full control of pricing, discounts, policies, certifications and eligibility.</p>
          <LegalLinks/>
        </div>
      </section>
    </div>
  );
}
